import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/v2/empty-state";

// ADV-799: section primitive for the Today page. Wraps shadcn Card with a
// header (title + optional count badge + optional action) and a collapsible
// body. Collapse state is ephemeral in-memory (per the plan "What We're NOT
// Doing"); no localStorage / backend persistence.

interface TodaySectionProps {
  id: string;
  title: string;
  /** Optional count rendered as a small monospace pill next to the title. */
  count?: number;
  /** Optional header-right action — e.g., "View all" link or button. */
  action?: ReactNode;
  /** Section body. When null/undefined an EmptyState is rendered. */
  children?: ReactNode;
}

export function TodaySection({
  id,
  title,
  count,
  action,
  children,
}: TodaySectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const hasBody = children !== undefined && children !== null;
  return (
    <section data-testid={id} className="mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 py-3">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
            aria-controls={`${id}-body`}
            className="flex flex-1 items-center gap-2 text-left"
          >
            {collapsed ? (
              <ChevronRight
                aria-hidden
                className="size-4 text-[color:var(--color-text-md)]"
              />
            ) : (
              <ChevronDown
                aria-hidden
                className="size-4 text-[color:var(--color-text-md)]"
              />
            )}
            <h2 className="typo-body font-medium text-[color:var(--color-text-hi)]">
              {title}
            </h2>
            {typeof count === "number" && (
              <span
                data-testid={`${id}-count`}
                className="rounded-md bg-[color:var(--color-surface-2)] px-1.5 py-0.5 text-xs tabular-nums text-[color:var(--color-text-md)]"
              >
                {count}
              </span>
            )}
          </button>
          {action ? <div className="shrink-0">{action}</div> : null}
        </CardHeader>
        {!collapsed && (
          <CardContent id={`${id}-body`} className="pt-0">
            {hasBody ? (
              children
            ) : (
              <EmptyState
                title="Nothing here yet"
                description="This section will populate as data flows in."
              />
            )}
          </CardContent>
        )}
      </Card>
    </section>
  );
}
